/* eslint-disable no-console */
import { ReactNode, useEffect, useMemo, useState, memo } from "react";
import {
  ColumnValuesMetric,
  CountMetric,
  DistanceToShoreMetric,
  DEFAULT_WATER_PATH_DISTANCE_KM,
  DistanceToFeaturesOverWaterMetric,
  Metric,
  MetricDependency,
  OverlayAreaMetric,
  OverlayAreaMetricValue,
  TotalAreaMetric,
  combineMetricsForFragments,
  getOverlayAreaClassTotals,
  getOverlayAreaOverlapCombineResult,
  isOverlayAreaClassKey,
  subjectIsFragment,
  subjectIsGeography,
  RasterStats,
  RasterOverlayAreaMetric,
  attachRasterOverlayAreaOverlapScope,
  getRasterOverlayAreaDisplayedClassValue,
  getRasterOverlayAreaOverlapCombineResult,
} from "overlay-engine";
import { useNumberFormatters } from "../hooks/useNumberFormatters";
import {
  ReportWidgetTooltipControls,
  TooltipInfoIcon,
  TooltipMorePopover,
  TooltipPopoverContent,
} from "../../editor/TooltipMenu";
import { LabeledDropdown } from "./LabeledDropdown";
import {
  DistanceToPointResolutionField,
  DistanceToPointSearchInputs,
  useDistanceToPointSearchDraft,
} from "./DistanceToPointSearchFields";
import { UnitSelector } from "./UnitSelector";
import {
  applyBufferSettingsToParameters,
  BufferSelector,
  getBufferSettingsFromDependencies,
} from "./BufferSelector";
import {
  AreaUnit,
  LengthUnit,
  getLocalizedUnitLabel,
  isAreaUnit,
  isLengthUnit,
} from "../utils/units";
import Skeleton from "../../components/Skeleton";
import {
  ReportWidget,
  ReportWidgetProps,
  TooltipBooleanConfigurationOption,
} from "./reportWidgetShared";
import { useBaseReportContext } from "../context/BaseReportContext";
import { useSubjectReportContext } from "../context/SubjectReportContext";
import { usePrimaryGeography } from "../hooks/usePrimaryGeography";
import type { SketchClassPrimaryGeoFields } from "../hooks/usePrimaryGeography";
import { MetricLoadingDots } from "../components/MetricLoadingDots";
import { NumberRoundingControl } from "./NumberRoundingControl";
import { VrmSelector } from "./VrmSelector";
import { SketchGeometryType } from "../../generated/graphql";
import { useTranslation } from "react-i18next";
import {
  useOverlaySources,
  useRelatedOverlay,
} from "../hooks/useOverlaySources";
import useCurrentLang from "../../useCurrentLang";
import * as Popover from "@radix-ui/react-popover";
import { Pencil2Icon } from "@radix-ui/react-icons";
import { GeostatsLayer, isGeostatsLayer } from "@seasketch/geostats-types";
import ColumnStatsWarning, {
  hasBufferedColumnValuesDependency,
} from "./ColumnStatsWarning";
import BufferedOverlapWarning from "./BufferedOverlapWarning";
import {
  attachOverlayAreaOverlapScope,
  fragmentMetricsTaggedWithGeography,
} from "./ClassTableRows";
import {
  getColumnTotalFromGeostats,
  getFeatureCountFromGeostats,
  listColumnsWithGeostatsTotals,
} from "./columnTotalFromGeostats";
import { numberColumnStatOrZero } from "./inlineColumnValues";

export type PluralizedMessages = Record<string, string>;
export type PluralizedMessagesByLang = Record<string, PluralizedMessages>;
export type ColumnValuesStatKey =
  | "mean"
  | "min"
  | "max"
  | "stdDev"
  | "sum"
  | "count"
  | "countDistinct";

const defaultPluralizedCountLabels = {
  en: {
    zero: "no features",
    one: "feature",
    other: "features",
  },
  es: {
    zero: "sin elementos",
    one: "elemento",
    other: "elementos",
  },
  dv: {
    zero: "ނެތް އެލިމެންޓް",
    one: "އެލިމެންޓް",
    other: "އެލިމެންޓް",
  },
  pt: {
    zero: "sem entidades",
    one: "entidade",
    other: "entidades",
  },
  "pt-br": {
    zero: "sem entidades",
    one: "entidade",
    other: "entidades",
  },
  no: {
    zero: "ingen objekter",
    one: "objekt",
    other: "objekter",
  },
  kos: {
    zero: "no features",
    one: "feature",
    other: "features",
  },
  sm: {
    zero: "no features",
    one: "feature",
    other: "features",
  },
  CHK: {
    zero: "no features",
    one: "feature",
    other: "features",
  },
  fj: {
    zero: "sega na iyaya",
    one: "iyaya",
    other: "veiyaya",
  },
  fh: {
    zero: "कोई फीचर नहीं",
    one: "फीचर",
    other: "फीचर",
  },
  haw: {
    zero: "ʻaʻohe mea",
    one: "mea",
    other: "nā mea",
  },
  fr: {
    zero: "aucune entité",
    one: "entité",
    other: "entités",
  },
  de: {
    zero: "keine Objekte",
    one: "Objekt",
    other: "Objekte",
  },
  gil: {
    zero: "no features",
    one: "feature",
    other: "features",
  },
  hr: {
    zero: "nema elemenata",
    one: "element",
    few: "elementa",
    other: "elemenata",
  },
  af: {
    zero: "geen elemente",
    one: "element",
    other: "elemente",
  },
  "zh-Hans": {
    other: "要素",
  },
  ar: {
    zero: "لا توجد ميزات",
    one: "ميزة",
    two: "ميزتان",
    few: "ميزات",
    many: "ميزات",
    other: "ميزات",
  },
  nl: {
    zero: "geen objecten",
    one: "object",
    other: "objecten",
  },
  it: {
    zero: "nessun elemento",
    one: "elemento",
    other: "elementi",
  },
  bg: {
    zero: "няма елементи",
    one: "елемент",
    other: "елементи",
  },
  "fr-be": {
    zero: "aucune entité",
    one: "entité",
    other: "entités",
  },
  el: {
    zero: "κανένα στοιχείο",
    one: "στοιχείο",
    other: "στοιχεία",
  },
  hi: {
    zero: "कोई तत्व नहीं",
    one: "तत्व",
    other: "तत्व",
  },
  id: {
    other: "fitur",
  },
  mi: {
    zero: "kāore he taonga",
    one: "taonga",
    other: "taonga",
  },
  pl: {
    zero: "brak elementów",
    one: "element",
    few: "elementy",
    many: "elementów",
    other: "elementów",
  },
  ro: {
    zero: "fără entități",
    one: "entitate",
    few: "entități",
    other: "entități",
  },
  to: {
    zero: "ʻikai ha meʻa",
    one: "meʻa",
    other: "meʻa",
  },
  zu: {
    zero: "azikho izinto",
    one: "into",
    other: "izinto",
  },
  sv: {
    zero: "inga objekt",
    one: "objekt",
    other: "objekt",
  },
  "sv-fi": {
    zero: "inga objekt",
    one: "objekt",
    other: "objekt",
  },
  et: {
    zero: "elemendid puuduvad",
    one: "element",
    other: "elementi",
  },
  lv: {
    zero: "nav elementu",
    one: "elements",
    few: "elementi",
    other: "elementu",
  },
  lt: {
    zero: "nėra elementų",
    one: "elementas",
    few: "elementai",
    many: "elementų",
    other: "elementų",
  },
  ru: {
    zero: "нет объектов",
    one: "объект",
    few: "объекта",
    many: "объектов",
    other: "объектов",
  },
  da: {
    zero: "ingen objekter",
    one: "objekt",
    other: "objekter",
  },
  yap: {
    zero: "no features",
    one: "feature",
    other: "features",
  },
  bi: {
    zero: "no features",
    one: "feature",
    other: "features",
  },
} as PluralizedMessagesByLang;

const defaultDistinctValueMessages: PluralizedMessages = {
  zero: "no distinct values",
  one: "distinct value",
  two: "distinct values",
  few: "distinct values",
  many: "distinct values",
  other: "distinct values",
};

type RasterValuesStatKey = "mean" | "min" | "max" | "sum" | "count";

const defaultPluralizedDistinctValueLabels = Object.fromEntries(
  Object.entries(defaultPluralizedCountLabels).map(([lang, messages]) => {
    const entries: Record<string, string> = {};
    Object.keys(messages).forEach((category) => {
      entries[category] =
        defaultDistinctValueMessages[category] ||
        defaultDistinctValueMessages.other;
    });
    return [lang, entries];
  })
) as PluralizedMessagesByLang;

export type InlineMetricComponentSettings = {
  unit?: AreaUnit | LengthUnit;
  unitDisplay?: "long" | "short";
  minimumFractionDigits: number;
  presentation:
    | "total_area"
    | "percent_area"
    | "distance_to_shore"
    | "distance_to_features_over_water"
    | "overlay_area"
    | "geography_overlay_area"
    | "count"
    | "percent_count"
    | "percent_count_total"
    | "column_values"
    | "percent_column_total_overlapped"
    | "raster_stats"
    | "geography_raster_stats"
    | "geography_proportion_captured"
    | "raster_overlay_area";
  stat?: ColumnValuesStatKey;
  rasterStat?: RasterValuesStatKey;
  hideLabelForCount?: boolean;
  pluralizedCountLabels?: PluralizedMessagesByLang;
  pluralizedDistinctValueLabels?: PluralizedMessagesByLang;
  column?: string;
  /** Point-feature property used as the place name, e.g. PORT_NAME. Null shows the distance only. */
  labelColumn?: string | null;
  /** For geography_overlay_area: geography id to report on. undefined/"auto" = primary clipping geography. */
  geographyId?: number | "auto";
};

const _InlineMetric: ReportWidget<InlineMetricComponentSettings> = ({
  metrics,
  sources,
  loading,
  errors,
  geographies,
  componentSettings,
  dependencies,
  marks,
  sketchClass,
  lang,
}) => {
  const { t } = useTranslation("reports");
  const { clippingGeography } = usePrimaryGeography(sketchClass, geographies);
  const subjectReportContext = useSubjectReportContext();
  const emptyCollectionWithoutFragments =
    !subjectReportContext.loading &&
    subjectReportContext.data?.isCollection === true &&
    (subjectReportContext.data.relatedFragments?.length ?? 0) === 0;
  const {
    pluralRules,
    countDefaultMessages,
    countCustomMessages,
    distinctDefaultMessages,
    distinctCustomMessages,
  } = useMemo(() => {
    const pluralRules = new Intl.PluralRules(lang);
    const countDefaultMessages =
      defaultPluralizedCountLabels[lang] || defaultPluralizedCountLabels.en;
    const countCustomMessages =
      componentSettings?.pluralizedCountLabels?.[lang];
    const distinctDefaultMessages =
      defaultPluralizedDistinctValueLabels[lang] ||
      defaultPluralizedDistinctValueLabels.en;
    const distinctCustomMessages =
      componentSettings?.pluralizedDistinctValueLabels?.[lang];
    return {
      pluralRules,
      countDefaultMessages,
      countCustomMessages,
      distinctDefaultMessages,
      distinctCustomMessages,
    };
  }, [
    lang,
    componentSettings?.pluralizedCountLabels,
    componentSettings?.pluralizedDistinctValueLabels,
  ]);
  if (
    sketchClass.geometryType !== SketchGeometryType.Polygon &&
    sketchClass.geometryType !== SketchGeometryType.Collection
  ) {
    throw new Error(
      "Inline metric only supports polygon and collection geometry types currently."
    );
  }
  const formatters = useNumberFormatters({
    unit: componentSettings?.unit,
    minimumFractionDigits: componentSettings?.minimumFractionDigits,
    unitDisplay: componentSettings?.unitDisplay || "short",
  });
  const rasterUnitLabel = useMemo(() => {
    const unit = componentSettings?.unit;
    if (!unit || (!isAreaUnit(unit) && !isLengthUnit(unit))) {
      return null;
    }
    return getLocalizedUnitLabel(
      unit,
      lang,
      isAreaUnit(unit),
      componentSettings?.unitDisplay || "short"
    );
  }, [componentSettings?.unit, componentSettings?.unitDisplay, lang]);

  const formattedValue = useMemo(() => {
    if (!dependencies.length) {
      throw new Error("No metric dependencies configured");
    }
    const presentation = componentSettings?.presentation || "total_area";
    if (errors.length > 0 || loading) {
      return null;
    }
    if (metrics.length === 0) {
      if (!emptyCollectionWithoutFragments) {
        return null;
      }
      switch (presentation) {
        case "total_area":
          return formatters.area(0);
        case "percent_area":
          return formatters.percent(0);
        case "distance_to_shore":
          return formatters.distance(0);
        case "distance_to_features_over_water":
          return formatters.distance(0);
        case "overlay_area":
          return formatters.area(0);
        case "geography_overlay_area":
          return formatters.area(0);
        case "count": {
          const count = 0;
          if (componentSettings?.hideLabelForCount) {
            return formatters.count(count);
          }
          const pluralKey = pluralRules.select(count) as string;
          const label =
            countCustomMessages?.[pluralKey] || countDefaultMessages[pluralKey];
          return `${formatters.count(count)} ${label}`;
        }
        case "percent_count":
          return formatters.percent(0);
        case "percent_count_total":
          return formatters.percent(0);
        case "percent_column_total_overlapped":
          return formatters.percent(0);
        case "column_values": {
          const statKey = (componentSettings?.stat || "mean") as
            | "mean"
            | "min"
            | "max"
            | "stdDev"
            | "sum"
            | "count"
            | "countDistinct";
          if (componentSettings?.stat === "countDistinct") {
            const countDistinct = 0;
            const pluralKey = pluralRules.select(countDistinct) as string;
            const label =
              distinctCustomMessages?.[pluralKey] ||
              distinctDefaultMessages[pluralKey];
            return `${formatters.count(countDistinct)} ${label}`;
          }
          if (statKey === "count") {
            return formatters.count(0);
          }
          return formatters.decimal(0);
        }
        case "raster_stats": {
          const formatted = formatters.decimal(0);
          return rasterUnitLabel
            ? `${formatted} ${rasterUnitLabel}`
            : formatted;
        }
        case "geography_raster_stats": {
          const formatted = formatters.decimal(0);
          return rasterUnitLabel
            ? `${formatted} ${rasterUnitLabel}`
            : formatted;
        }
        case "geography_proportion_captured":
          return formatters.percent(0);
        case "raster_overlay_area":
          return formatters.area(0);
        default:
          // eslint-disable-next-line i18next/no-literal-string
          errors.push(`Unsupported presentation: ${presentation}`);
          return null;
      }
    }
    switch (presentation) {
      case "total_area":
        const combined = combineMetricsForFragments(
          metrics as Pick<Metric, "type" | "value">[],
          "total_area"
        ) as TotalAreaMetric;
        return formatters.area(combined.value);
      case "percent_area":
        if (!clippingGeography) {
          throw new Error(
            "Primary geography for displaying percent area not found."
          );
        }

        // Should be percent of sketch class' clipping geography
        const totalArea = combineMetricsForFragments(
          metrics.filter((m) => subjectIsFragment(m.subject)) as Pick<
            Metric,
            "type" | "value"
          >[],
          "total_area"
        ) as TotalAreaMetric;
        const geographyAreaMetric = metrics.find(
          (m) =>
            subjectIsGeography(m.subject) &&
            m.subject.id === clippingGeography?.id
        );
        if (!geographyAreaMetric) {
          throw new Error("Primary geography not found in metrics.");
        }
        return formatters.percent(totalArea.value / geographyAreaMetric.value);
      case "distance_to_shore": {
        const distanceToShore = metrics.find(
          (m) => m.type === "distance_to_shore"
        );
        const combined = combineMetricsForFragments(
          metrics as Pick<Metric, "type" | "value">[],
          "distance_to_shore"
        ) as DistanceToShoreMetric;
        if (!distanceToShore) {
          console.error("Distance to shore not found in metrics.", metrics);
          throw new Error("Distance to shore not found in metrics.");
        }
        return formatters.distance(combined.value.meters / 1000);
      }
      case "distance_to_features_over_water": {
        const combined = combineMetricsForFragments(
          metrics as Pick<Metric, "type" | "value">[],
          "distance_to_features_over_water"
        ) as DistanceToFeaturesOverWaterMetric;
        const nearest = combined.value.features[0];
        if (!nearest || !Number.isFinite(nearest.meters)) {
          const dependency = dependencies.find(
            (item) => item.type === "distance_to_features_over_water"
          );
          const rawLimit = dependency?.parameters?.maxDistanceKm;
          const limitKm =
            typeof rawLimit === "number" &&
            Number.isFinite(rawLimit) &&
            rawLimit > 0
              ? rawLimit
              : DEFAULT_WATER_PATH_DISTANCE_KM;
          return t("beyond {{distance}}", {
            distance: formatters.distance(limitKm),
          });
        }
        const distanceText = formatters.distance(nearest.meters / 1000);
        const labelColumn = componentSettings?.labelColumn;
        const rawLabel = labelColumn
          ? nearest.properties?.[labelColumn]
          : undefined;
        const name =
          typeof rawLabel === "string" && rawLabel.trim()
            ? rawLabel.trim()
            : null;
        return name
          ? t("{{distance}} from {{name}}", {
              distance: distanceText,
              name,
            })
          : distanceText;
      }
      case "overlay_area": {
        const overlayMetrics = metrics.filter(
          (m) => m.type === "overlay_area" && subjectIsFragment(m.subject)
        );
        if (overlayMetrics.length === 0) {
          throw new Error("Overlay area not found in metrics.");
        }
        // Corrected totals + optional residual-uncertainty metadata.
        // @see OverlayAreaOverlapInfo
        let combined = combineMetricsForFragments(
          overlayMetrics as Pick<Metric, "type" | "value">[],
          "overlay_area"
        ) as OverlayAreaMetric;
        combined = attachOverlayAreaOverlapScope(
          combined,
          overlayMetrics
        ) as OverlayAreaMetric;
        const totals = getOverlayAreaClassTotals(combined.value);
        return formatters.area(totals["*"] ?? 0);
      }
      case "geography_overlay_area": {
        const geographyId =
          componentSettings.geographyId === "auto" ||
          componentSettings.geographyId === undefined
            ? clippingGeography?.id
            : componentSettings.geographyId;
        if (geographyId === undefined) {
          throw new Error("Primary geography not found.");
        }
        const geographyOverlayMetric = metrics.find(
          (m) =>
            m.type === "overlay_area" &&
            subjectIsGeography(m.subject) &&
            m.subject.id === geographyId
        ) as OverlayAreaMetric | undefined;
        if (!geographyOverlayMetric) {
          throw new Error("Geography overlay area not found in metrics.");
        }
        const totals = getOverlayAreaClassTotals(geographyOverlayMetric.value);
        const totalArea =
          totals["*"] ??
          Object.entries(geographyOverlayMetric.value).reduce(
            (sum, [key, v]) =>
              isOverlayAreaClassKey(key) && typeof v === "number"
                ? sum + v
                : sum,
            0
          );
        return formatters.area(totalArea);
      }
      case "count": {
        const combined = combineMetricsForFragments(
          metrics as Pick<Metric, "type" | "value">[],
          "count"
        ) as CountMetric;
        const count = combined.value["*"].count;
        if (componentSettings?.hideLabelForCount) {
          return formatters.count(count);
        } else {
          const pluralKey = pluralRules.select(count) as string;

          let label =
            countCustomMessages?.[pluralKey] || countDefaultMessages[pluralKey];
          return `${formatters.count(count)} ${label}`;
        }
      }
      case "percent_count": {
        const geographyId =
          componentSettings.geographyId === "auto" ||
          componentSettings.geographyId === undefined
            ? clippingGeography?.id
            : componentSettings.geographyId;
        if (geographyId === undefined) {
          throw new Error(
            "Primary geography for displaying percent of geography count not found."
          );
        }
        const combined = combineMetricsForFragments(
          fragmentMetricsTaggedWithGeography(
            metrics,
            geographyId,
            "count"
          ) as Pick<Metric, "type" | "value">[],
          "count"
        ) as CountMetric;
        const count = combined.value["*"]?.count ?? 0;
        const geographyCountMetric = metrics.find(
          (m) =>
            m.type === "count" &&
            subjectIsGeography(m.subject) &&
            m.subject.id === geographyId
        ) as CountMetric | undefined;
        if (!geographyCountMetric) {
          throw new Error("Geography count not found in metrics.");
        }
        const geographyCount = geographyCountMetric.value["*"]?.count ?? 0;
        if (!geographyCount) {
          return formatters.percent(0);
        }
        return formatters.percent(count / geographyCount);
      }
      case "percent_count_total": {
        const combined = combineMetricsForFragments(
          metrics.filter(
            (m) => m.type === "count" && subjectIsFragment(m.subject)
          ) as Pick<Metric, "type" | "value">[],
          "count"
        ) as CountMetric;
        const count = combined.value["*"]?.count ?? 0;
        const layerTotal = getFeatureCountFromGeostats(sources?.[0]?.geostats);
        if (layerTotal === null) {
          throw new Error(
            "Feature count not available in layer statistics (geostats)."
          );
        }
        if (!layerTotal) {
          return formatters.percent(0);
        }
        return formatters.percent(count / layerTotal);
      }
      case "percent_column_total_overlapped": {
        const columnValues = metrics.filter(
          (m) => m.type === "column_values" && subjectIsFragment(m.subject)
        );
        if (!columnValues.length) {
          throw new Error("Column values not found in metrics.");
        }
        const combined = combineMetricsForFragments(
          columnValues as Pick<Metric, "type" | "value">[],
          "column_values"
        ) as ColumnValuesMetric;
        const prop = componentSettings?.column || "";
        const sum = numberColumnStatOrZero(combined.value["*"], prop, "sum");
        const columnTotal = getColumnTotalFromGeostats(
          sources?.[0]?.geostats,
          prop
        );
        if (columnTotal === null) {
          throw new Error(
            "Column total not available in layer statistics (geostats)."
          );
        }
        if (columnTotal === 0) {
          return formatters.percent(0);
        }
        return formatters.percent(sum / columnTotal);
      }
      case "column_values": {
        const columnValues = metrics.filter(
          (m) => m.type === "column_values" && subjectIsFragment(m.subject)
        );
        if (!columnValues.length) {
          throw new Error("Column values not found in metrics.");
        }
        const combined = combineMetricsForFragments(
          columnValues as Pick<Metric, "type" | "value">[],
          "column_values"
        ) as ColumnValuesMetric;
        const prop = componentSettings?.column || "";
        const statKey = (componentSettings?.stat || "mean") as
          | "mean"
          | "min"
          | "max"
          | "stdDev"
          | "sum"
          | "count"
          | "countDistinct";
        const values = combined.value["*"];
        if (componentSettings?.stat === "countDistinct") {
          const countDistinct = numberColumnStatOrZero(
            values,
            prop,
            "countDistinct"
          );
          const pluralKey = pluralRules.select(countDistinct) as string;
          const label =
            distinctCustomMessages?.[pluralKey] ||
            distinctDefaultMessages[pluralKey];
          return `${formatters.count(countDistinct)} ${label}`;
        }
        const value = numberColumnStatOrZero(values, prop, statKey);
        if (componentSettings?.stat === "count") {
          return value;
        }
        return formatters.decimal(value);
      }
      case "raster_stats": {
        const rasterStats = metrics.find((m) => m.type === "raster_stats");
        if (!rasterStats) {
          throw new Error("Raster stats not found in metrics.");
        }
        const combined = combineMetricsForFragments(
          metrics as Pick<Metric, "type" | "value">[],
          "raster_stats"
        ) as RasterStats;
        const value =
          combined.value.bands[0][componentSettings?.rasterStat || "mean"];
        const formatted = formatters.decimal(value);
        return rasterUnitLabel ? `${formatted} ${rasterUnitLabel}` : formatted;
      }
      case "geography_raster_stats": {
        const geographyId =
          componentSettings.geographyId === "auto" ||
          componentSettings.geographyId === undefined
            ? clippingGeography?.id
            : componentSettings.geographyId;
        if (geographyId === undefined) {
          throw new Error("Primary geography not found.");
        }
        const geographyRasterMetric = metrics.find(
          (m) =>
            m.type === "raster_stats" &&
            subjectIsGeography(m.subject) &&
            m.subject.id === geographyId
        ) as RasterStats | undefined;
        if (!geographyRasterMetric) {
          throw new Error("Geography raster stats not found in metrics.");
        }
        const bands = geographyRasterMetric.value.bands;
        if (!bands || bands.length === 0) {
          throw new Error("No raster band data available for this geography.");
        }
        const value = bands[0][componentSettings?.rasterStat || "mean"];
        const formatted = formatters.decimal(value);
        return rasterUnitLabel ? `${formatted} ${rasterUnitLabel}` : formatted;
      }
      case "geography_proportion_captured": {
        const geographyId =
          componentSettings.geographyId === "auto" ||
          componentSettings.geographyId === undefined
            ? clippingGeography?.id
            : componentSettings.geographyId;
        if (geographyId === undefined) {
          throw new Error("Primary geography not found.");
        }
        const overlayFragmentMetrics = fragmentMetricsTaggedWithGeography(
          metrics,
          geographyId,
          "overlay_area"
        );
        const overlayGeographyMetric = metrics.find(
          (m) =>
            m.type === "overlay_area" &&
            subjectIsGeography(m.subject) &&
            m.subject.id === geographyId
        ) as OverlayAreaMetric | undefined;
        const hasOverlayArea =
          overlayGeographyMetric != null ||
          metrics.some((m) => m.type === "overlay_area");
        if (hasOverlayArea) {
          if (!overlayGeographyMetric) {
            throw new Error("Geography overlay area not found in metrics.");
          }
          let combined = combineMetricsForFragments(
            overlayFragmentMetrics as Pick<Metric, "type" | "value">[],
            "overlay_area"
          ) as OverlayAreaMetric;
          combined = attachOverlayAreaOverlapScope(
            combined,
            overlayFragmentMetrics
          ) as OverlayAreaMetric;
          const sketchArea =
            getOverlayAreaClassTotals(combined.value)["*"] ?? 0;
          const geographyTotals = getOverlayAreaClassTotals(
            overlayGeographyMetric.value
          );
          const geographyArea =
            geographyTotals["*"] ??
            Object.entries(overlayGeographyMetric.value).reduce(
              (sum, [key, v]) =>
                isOverlayAreaClassKey(key) && typeof v === "number"
                  ? sum + v
                  : sum,
              0
            );
          if (!geographyArea) {
            return formatters.percent(0);
          }
          return formatters.percent(sketchArea / geographyArea);
        }
        // Sketch sum: combine fragment raster_stats metrics
        const fragmentRasterMetrics = fragmentMetricsTaggedWithGeography(
          metrics,
          geographyId,
          "raster_stats"
        );
        const combinedSketch = combineMetricsForFragments(
          fragmentRasterMetrics as Pick<Metric, "type" | "value">[],
          "raster_stats"
        ) as RasterStats;
        const sketchBands = combinedSketch.value.bands;
        if (!sketchBands || sketchBands.length === 0) {
          throw new Error("No raster band data available for sketch.");
        }
        const sketchSum = sketchBands[0].sum;
        // Geography sum: find the raster_stats metric for the target geography
        const geographyRasterMetric = metrics.find(
          (m) =>
            m.type === "raster_stats" &&
            subjectIsGeography(m.subject) &&
            m.subject.id === geographyId
        ) as RasterStats | undefined;
        if (!geographyRasterMetric) {
          throw new Error("Geography raster stats not found in metrics.");
        }
        const geographyBands = geographyRasterMetric.value.bands;
        if (!geographyBands || geographyBands.length === 0) {
          throw new Error("No raster band data available for this geography.");
        }
        const geographySum = geographyBands[0].sum;
        if (!geographySum) {
          return formatters.percent(0);
        }
        return formatters.percent(sketchSum / geographySum);
      }
      case "raster_overlay_area": {
        const overlayMetrics = metrics.filter(
          (m) =>
            m.type === "raster_overlay_area" && subjectIsFragment(m.subject)
        );
        if (overlayMetrics.length === 0) {
          throw new Error("Raster overlay area not found in metrics.");
        }
        let combined = combineMetricsForFragments(
          overlayMetrics as Pick<Metric, "type" | "value">[],
          "raster_overlay_area"
        ) as RasterOverlayAreaMetric;
        combined = attachRasterOverlayAreaOverlapScope(
          combined,
          overlayMetrics
        ) as RasterOverlayAreaMetric;
        return formatters.area(
          getRasterOverlayAreaDisplayedClassValue(combined.value, "*")
        );
      }
      default:
        // eslint-disable-next-line i18next/no-literal-string
        errors.push(`Unsupported presentation: ${presentation}`);
    }
  }, [
    dependencies,
    loading,
    emptyCollectionWithoutFragments,
    metrics,
    componentSettings?.presentation,
    componentSettings?.stat,
    componentSettings?.rasterStat,
    formatters,
    rasterUnitLabel,
    errors,
    pluralRules,
    componentSettings?.hideLabelForCount,
    countDefaultMessages,
    countCustomMessages,
    distinctCustomMessages,
    distinctDefaultMessages,
    componentSettings?.column,
    componentSettings?.labelColumn,
    t,
    clippingGeography,
    componentSettings?.geographyId,
    lang,
    sources,
  ]);

  // Combined fragment stats for the selected column, used to surface
  // accuracy warnings (entriesTruncated / weightsMayOverlap) next to the
  // displayed value. Geography-subject stats are intentionally excluded.
  const columnStatsForWarning = useMemo(() => {
    const presentation = componentSettings?.presentation || "total_area";
    if (
      loading ||
      errors.length > 0 ||
      (presentation !== "column_values" &&
        presentation !== "percent_column_total_overlapped")
    ) {
      return null;
    }
    const columnValues = metrics.filter(
      (m) => m.type === "column_values" && subjectIsFragment(m.subject)
    );
    if (!columnValues.length) {
      return null;
    }
    try {
      const combined = combineMetricsForFragments(
        columnValues as Pick<Metric, "type" | "value">[],
        "column_values"
      ) as ColumnValuesMetric;
      return combined.value["*"]?.[componentSettings?.column || ""] || null;
    } catch {
      return null;
    }
  }, [
    loading,
    errors,
    metrics,
    componentSettings?.presentation,
    componentSettings?.column,
  ]);

  const columnStatsWarningDisplayedStats = useMemo(() => {
    const presentation = componentSettings?.presentation || "total_area";
    if (presentation === "percent_column_total_overlapped") {
      return ["sum"];
    }
    return [componentSettings?.stat || "mean"];
  }, [componentSettings?.presentation, componentSettings?.stat]);

  // Buffered overlay_area / raster_overlay_area residual-uncertainty warning
  // (exact corrections stay silent). Skip entirely when no buffer — avoids a
  // second combine on the unbuffered path.
  // @see OverlayAreaOverlapInfo
  const overlayOverlapForWarning = useMemo((): {
    overcountMin: number;
    overcountMax: number;
    overcountEstimate?: number;
    total: number;
  } | null => {
    const presentation = componentSettings?.presentation || "total_area";
    const metricType =
      presentation === "raster_overlay_area"
        ? "raster_overlay_area"
        : presentation === "overlay_area" ||
          (presentation === "geography_proportion_captured" &&
            (dependencies || []).some((d) => d.type === "overlay_area"))
        ? "overlay_area"
        : null;
    if (loading || errors.length > 0 || !metricType) {
      return null;
    }
    const isBuffered = (dependencies || []).some(
      (d) =>
        d.type === metricType &&
        typeof d.parameters?.bufferDistanceKm === "number" &&
        d.parameters.bufferDistanceKm > 0
    );
    if (!isBuffered) {
      return null;
    }
    const overlayMetrics = metrics.filter(
      (m) => m.type === metricType && subjectIsFragment(m.subject)
    );
    if (!overlayMetrics.length) {
      return null;
    }
    try {
      if (metricType === "raster_overlay_area") {
        let combined = combineMetricsForFragments(
          overlayMetrics as Pick<Metric, "type" | "value">[],
          "raster_overlay_area"
        ) as RasterOverlayAreaMetric;
        combined = attachRasterOverlayAreaOverlapScope(
          combined,
          overlayMetrics
        ) as RasterOverlayAreaMetric;
        const combine = getRasterOverlayAreaOverlapCombineResult(
          combined.value
        );
        if (!combine?.perClass?.["*"]) {
          return null;
        }
        return {
          overcountMin: combine.perClass["*"].overcountMin,
          overcountMax: combine.perClass["*"].overcountMax,
          overcountEstimate: combine.perClass["*"].overcountEstimate,
          total: combine.perClass["*"].naiveSum,
        };
      }
      let combined = combineMetricsForFragments(
        overlayMetrics as Pick<Metric, "type" | "value">[],
        "overlay_area"
      ) as OverlayAreaMetric;
      combined = attachOverlayAreaOverlapScope(
        combined,
        overlayMetrics
      ) as OverlayAreaMetric;
      const combine = getOverlayAreaOverlapCombineResult(
        combined.value as OverlayAreaMetricValue
      );
      if (!combine?.perClass?.["*"]) {
        return null;
      }
      return {
        overcountMin: combine.perClass["*"].overcountMin,
        overcountMax: combine.perClass["*"].overcountMax,
        total: combine.perClass["*"].naiveSum,
      };
    } catch {
      return null;
    }
  }, [
    loading,
    errors,
    metrics,
    dependencies,
    componentSettings?.presentation,
  ]);

  if (loading) {
    return (
      <div className="inline-block rounded border border-blue-600/30 w-12 h-content relative -mb-[3px] bg-blue-500/20">
        <Skeleton
          strong
          className="inline-block border rounded absolute w-full h-full z-10"
        />
        <span className="absolute top-0 left-0 w-full h-full flex items-center justify-center z-20">
          <MetricLoadingDots className="" />
        </span>
        <span className="metric-loading-placeholder-for-height">20</span>
      </div>
    );
  } else if (errors.length > 0) {
    return (
      <span className="metric font-semibold rounded-sm inline-block">
        {errors.join(". \n")}
      </span>
    );
  } else {
    const underline =
      marks?.some((m) => m.type.name === "underline") ||
      marks?.some((m) => m.type.name === "link" && m.attrs?.underline);
    return (
      <span
        className={`metric font-semibold rounded-sm inline-block ${
          underline ? "underline" : ""
        }`}
        style={underline ? { textDecorationStyle: "solid" } : undefined}
      >
        {formattedValue}
        {columnStatsForWarning && (
          <ColumnStatsWarning
            stats={columnStatsForWarning}
            displayedStats={columnStatsWarningDisplayedStats}
            buffered={hasBufferedColumnValuesDependency(dependencies)}
            className="ml-0.5"
          />
        )}
        {overlayOverlapForWarning && (
          <BufferedOverlapWarning
            overcountMin={overlayOverlapForWarning.overcountMin}
            overcountMax={overlayOverlapForWarning.overcountMax}
            overcountEstimate={overlayOverlapForWarning.overcountEstimate}
            total={overlayOverlapForWarning.total}
            formatArea={(sqKm) => formatters.area(sqKm)}
            className="ml-0.5"
          />
        )}
      </span>
    );
  }
};

function inlineMetricPropsEqual(
  prevProps: ReportWidgetProps<InlineMetricComponentSettings>,
  nextProps: ReportWidgetProps<InlineMetricComponentSettings>
) {
  const changes: string[] = [];

  if (prevProps.componentSettings !== nextProps.componentSettings) {
    changes.push("componentSettings");
  }
  if (prevProps.metrics !== nextProps.metrics) {
    changes.push("metrics");
  }
  if (prevProps.dependencies !== nextProps.dependencies) {
    changes.push("dependencies");
  }
  if (prevProps.loading !== nextProps.loading) {
    changes.push("loading");
  }
  if (prevProps.errors !== nextProps.errors) {
    changes.push("errors");
  }
  if (prevProps.sources !== nextProps.sources) {
    changes.push("sources");
  }
  if (prevProps.geographies !== nextProps.geographies) {
    changes.push("geographies");
  }
  if (prevProps.marks !== nextProps.marks) {
    changes.push("marks");
  }
  if (prevProps.sketchClass !== nextProps.sketchClass) {
    changes.push("sketchClass");
  }
  if (prevProps.lang !== nextProps.lang) {
    changes.push("lang");
  }
  if (prevProps.node !== nextProps.node) {
    changes.push("node");
  }
  if (
    prevProps.alternateLanguageSettings !== nextProps.alternateLanguageSettings
  ) {
    changes.push("alternateLanguageSettings");
  }

  return changes.length === 0;
}

export const InlineMetric = memo(_InlineMetric, inlineMetricPropsEqual);

export function GeographySelector({
  geographies,
  clippingGeography,
  value,
  onChange,
  t,
  allowNone,
}: {
  geographies: Pick<{ id: number; name: string }, "id" | "name">[];
  clippingGeography:
    | Pick<{ id: number; name: string }, "id" | "name">
    | undefined;
  value: number | "auto" | null | undefined;
  onChange: (geographyId: number | "auto" | null | undefined) => void;
  t: (key: string) => string;
  /** When true, adds a "None" option (value `null`) — hides % of geography. */
  allowNone?: boolean;
}) {
  const autoLabelWithTooltip = useMemo(
    () => (
      <div className="flex items-center justify-between w-full -ml-2 pl-2">
        <span className="flex-1">{t("auto")}</span>
        <TooltipInfoIcon
          side="right"
          content={
            <div className="space-y-1.5">
              <div>
                {t(
                  "When 'auto' is specified, the geography used to clip the sketch will be used."
                )}
              </div>
              {clippingGeography && (
                <div className="font-semibold pt-1">
                  {t("Current:")} {clippingGeography.name}
                </div>
              )}
            </div>
          }
          className="-mr-1"
        />
      </div>
    ),
    [t, clippingGeography]
  );

  const options = useMemo(
    () => [
      ...(allowNone
        ? [
            {
              value: "none" as const,
              label: t("None"),
            },
          ]
        : []),
      {
        value: "auto" as const,
        label: autoLabelWithTooltip,
      },
      ...geographies.map((g) => ({
        value: String(g.id),
        label: g.name,
      })),
    ],
    [geographies, autoLabelWithTooltip, allowNone, t]
  );

  const dropdownValue =
    value === null
      ? "none"
      : value === undefined || value === "auto"
        ? "auto"
        : String(value);

  return (
    <LabeledDropdown
      label={t("Geography")}
      value={dropdownValue}
      // title={t("Geography")}
      options={options}
      getDisplayLabel={(selected) => {
        if (selected?.value === "none") {
          return t("None");
        }
        if (selected?.value === "auto") {
          return t("auto");
        }
        return selected?.label;
      }}
      onChange={(next) => {
        if (next === "none") {
          onChange(null);
        } else if (next === "auto") {
          onChange(undefined);
        } else {
          const parsed = Number(next);
          onChange(Number.isNaN(parsed) ? undefined : parsed);
        }
      }}
      ariaLabel={t("Geography")}
    />
  );
}

export const InlineMetricTooltipControls: ReportWidgetTooltipControls = ({
  node,
  onUpdate,
  onUpdateDependencyParameters,
}) => {
  const presentation =
    node.attrs.componentSettings.presentation || "total_area";
  const componentSettings = useMemo(() => {
    return (node.attrs?.componentSettings ||
      {}) as InlineMetricComponentSettings;
  }, [node.attrs?.componentSettings]);
  const unit = componentSettings.unit || "kilometer";
  const unitDisplay = componentSettings.unitDisplay || "short";
  const i18n = useTranslation("admin:reports");
  const t = i18n.t;

  const type = formatPresentationLabel(componentSettings.presentation);
  const lang = useCurrentLang();
  const pluralCategories = useMemo(() => {
    return new Intl.PluralRules(lang.code).resolvedOptions().pluralCategories;
  }, [lang.code]);
  const { defaultMessages, customMessages } = useMemo(() => {
    const defaultMessages =
      defaultPluralizedCountLabels[lang.code] ||
      defaultPluralizedCountLabels.en;
    const customMessages =
      componentSettings?.pluralizedCountLabels?.[lang.code];
    return { defaultMessages, customMessages };
  }, [lang.code, componentSettings?.pluralizedCountLabels]);
  const { distinctDefaultMessages, distinctCustomMessages } = useMemo(() => {
    const distinctDefaultMessages =
      defaultPluralizedDistinctValueLabels[lang.code] ||
      defaultPluralizedDistinctValueLabels.en;
    const distinctCustomMessages =
      componentSettings?.pluralizedDistinctValueLabels?.[lang.code];
    return { distinctDefaultMessages, distinctCustomMessages };
  }, [lang.code, componentSettings?.pluralizedDistinctValueLabels]);

  const dependencies = useMemo(
    () => (node.attrs?.metrics || []) as MetricDependency[],
    [node.attrs?.metrics]
  );
  const waterPathSearch = useDistanceToPointSearchDraft({
    metrics: dependencies,
    onUpdateDependencyParameters,
    showMaxMatches: false,
    active: presentation === "distance_to_features_over_water",
  });

  const { filteredSources: sources } = useOverlaySources(dependencies);
  const relatedOverlay = useRelatedOverlay(dependencies);
  const pointLabelColumnOptions = useMemo(() => {
    if (presentation !== "distance_to_features_over_water") return [];
    const layer = (sources?.[0]?.geostats as { layers?: unknown[] } | null)
      ?.layers?.[0];
    if (!isGeostatsLayer(layer) || !layer.attributes) return [];
    return layer.attributes
      .map((attr) => attr.attribute)
      .filter((name) => name.length > 0)
      .sort()
      .map((name) => ({ value: name, label: name }));
  }, [presentation, sources]);

  const bufferSettings = useMemo(
    () => getBufferSettingsFromDependencies(dependencies),
    [dependencies]
  );
  const showBufferGeography = useMemo(
    () => dependencies.some((d) => d.subjectType === "geographies"),
    [dependencies]
  );
  const isOverlayGeographyProportion = useMemo(
    () =>
      presentation === "geography_proportion_captured" &&
      dependencies.some((d) => d.type === "overlay_area"),
    [presentation, dependencies]
  );
  const isRasterGeographyProportion = useMemo(
    () =>
      presentation === "geography_proportion_captured" &&
      dependencies.some((d) => d.type === "raster_stats"),
    [presentation, dependencies]
  );

  const { geographies } = useBaseReportContext();
  const subjectReportContext = useSubjectReportContext();
  const tooltipSketchClass = subjectReportContext.data?.sketch?.sketchClass;
  const sketchClassForPrimaryGeography: SketchClassPrimaryGeoFields =
    tooltipSketchClass ?? {
      geometryType: SketchGeometryType.Polygon,
      clippingGeographies: [],
      validChildren: [],
      project: { sketchClasses: [] },
    };
  const { clippingGeography } = usePrimaryGeography(
    sketchClassForPrimaryGeography,
    geographies
  );

  const { valueColumnOptions, valueColumnAttributesByName } = useMemo(() => {
    const options: Array<{ value: string; label: ReactNode }> = [];
    const valueColumnAttributesByName: Record<string, { type?: string }> = {};

    if (
      presentation !== "column_values" &&
      presentation !== "percent_column_total_overlapped"
    ) {
      return { valueColumnOptions: options, valueColumnAttributesByName };
    }

    const source = sources?.[0];
    if (!source?.geostats) {
      return { valueColumnOptions: options, valueColumnAttributesByName };
    }

    const geoLayer = isGeostatsLayer(
      (source.geostats as any)?.layers?.[0] as GeostatsLayer
    )
      ? ((source.geostats as any).layers[0] as GeostatsLayer)
      : undefined;

    if (!geoLayer?.attributes) {
      return { valueColumnOptions: options, valueColumnAttributesByName };
    }

    const columnsWithTotals =
      presentation === "percent_column_total_overlapped"
        ? new Set(listColumnsWithGeostatsTotals(source.geostats))
        : null;

    for (const attr of geoLayer.attributes) {
      if (columnsWithTotals && !columnsWithTotals.has(attr.attribute)) {
        continue;
      }
      valueColumnAttributesByName[attr.attribute] = { type: attr.type };
      const exampleValues = Object.keys(attr.values || {})
        .slice(0, 5)
        .map((v) => String(v));
      const examplesText =
        exampleValues.length > 0 ? exampleValues.join(", ") : "";

      options.push({
        value: attr.attribute,
        label: (
          <div className="flex flex-col">
            <span className="font-medium">{attr.attribute}</span>
            {examplesText && (
              <span className="text-xs text-gray-500 truncate max-w-[200px]">
                {examplesText}
              </span>
            )}
          </div>
        ),
      });
    }

    if (
      componentSettings.column &&
      !options.some((opt) => opt.value === componentSettings.column)
    ) {
      options.unshift({
        value: componentSettings.column,
        label: (
          <div className="flex flex-col">
            <span className="font-medium">{componentSettings.column}</span>
            <span className="text-xs text-gray-500 truncate max-w-[200px]">
              {t("Current selection")}
            </span>
          </div>
        ),
      });
    }

    return { valueColumnOptions: options, valueColumnAttributesByName };
  }, [presentation, sources, componentSettings.column, t]);

  const selectedValueColumnIsNumeric =
    componentSettings.column &&
    valueColumnAttributesByName[componentSettings.column]?.type === "number";

  const statOptions = useMemo(
    () => [
      {
        value: "mean",
        label: t("Mean"),
        disabled: !selectedValueColumnIsNumeric,
      },
      {
        value: "min",
        label: t("Minimum"),
        disabled: !selectedValueColumnIsNumeric,
      },
      {
        value: "max",
        label: t("Maximum"),
        disabled: !selectedValueColumnIsNumeric,
      },
      {
        value: "stdDev",
        label: t("Standard deviation"),
        disabled: !selectedValueColumnIsNumeric,
      },
      {
        value: "sum",
        label: t("Sum"),
        disabled: !selectedValueColumnIsNumeric,
      },
      { value: "countDistinct", label: t("Distinct values") },
    ],
    [t, selectedValueColumnIsNumeric]
  );

  const rasterStatOptions = useMemo(
    () => [
      { value: "mean", label: t("Mean") },
      { value: "min", label: t("Minimum") },
      { value: "max", label: t("Maximum") },
      { value: "sum", label: t("Sum") },
      { value: "count", label: t("Count") },
    ],
    [t]
  );

  const selectedStat: ColumnValuesStatKey = selectedValueColumnIsNumeric
    ? (componentSettings.stat as ColumnValuesStatKey) ||
      ("mean" as ColumnValuesStatKey)
    : ("countDistinct" as ColumnValuesStatKey);

  const selectedRasterStat: RasterValuesStatKey =
    (componentSettings.rasterStat as RasterValuesStatKey) || "mean";

  useEffect(() => {
    if (presentation !== "column_values") return;
    if (selectedValueColumnIsNumeric) return;
    if (componentSettings?.stat === "countDistinct") return;

    onUpdate({
      componentSettings: {
        ...componentSettings,
        stat: "countDistinct",
      },
    });
  }, [presentation, selectedValueColumnIsNumeric, componentSettings, onUpdate]);

  const handleValueColumnChange = (value: string) => {
    onUpdate({
      componentSettings: { ...componentSettings, column: value },
    });

    // Scope the underlying metric dependency to the selected column. Metrics
    // scoped via includedColumns retain per-feature records, allowing
    // statistics to be combined exactly across fragments without
    // double-counting features that span fragment boundaries.
    onUpdateDependencyParameters((dependency) =>
      dependency.type === "column_values"
        ? { ...dependency.parameters, includedColumns: [value] }
        : { ...dependency.parameters }
    );

    if (presentation === "percent_column_total_overlapped") {
      return;
    }

    const nextValueColumnIsNumeric =
      valueColumnAttributesByName[value]?.type === "number";
    if (
      !nextValueColumnIsNumeric &&
      componentSettings?.stat !== "countDistinct"
    ) {
      onUpdate({
        componentSettings: { ...componentSettings, stat: "countDistinct" },
      });
    }
  };

  const handleStatChange = (stat: ColumnValuesStatKey) => {
    if (!selectedValueColumnIsNumeric && stat !== "countDistinct") {
      return;
    }
    onUpdate({
      componentSettings: { ...componentSettings, stat },
    });
  };

  const handleRasterStatChange = (stat: RasterValuesStatKey) => {
    onUpdate({
      componentSettings: { ...componentSettings, rasterStat: stat },
    });
  };

  const currentVrm = useMemo(() => {
    const fragmentDep = dependencies.find((d) => d.subjectType === "fragments");
    return fragmentDep?.parameters?.vrm;
  }, [dependencies]);

  const currentGeographyVrm = useMemo(() => {
    const geographyDep = dependencies.find(
      (d) => d.subjectType === "geographies"
    );
    return geographyDep?.parameters?.vrm;
  }, [dependencies]);

  const handleVrmChange = (next: false | "auto" | number | undefined) => {
    onUpdateDependencyParameters((dependency) => {
      const params = { ...(dependency.parameters || {}) };
      if (dependency.subjectType !== "fragments") {
        return params;
      }
      if (next === undefined) {
        delete params.vrm;
      } else {
        params.vrm = next;
      }
      return params;
    });
  };

  const handleGeographyVrmChange = (
    next: false | "auto" | number | undefined
  ) => {
    onUpdateDependencyParameters((dependency) => {
      const params = { ...(dependency.parameters || {}) };
      if (dependency.subjectType !== "geographies") {
        return params;
      }
      if (next === undefined) {
        delete params.vrm;
      } else {
        params.vrm = next;
      }
      return params;
    });
  };

  const [countLabelsModalOpen, setCountLabelsModalOpen] = useState(false);
  const [distinctLabelsModalOpen, setDistinctLabelsModalOpen] = useState(false);

  // TODO: Add related geography once supported

  return (
    <>
      {[
        "total_area",
        "overlay_area",
        "geography_overlay_area",
        "raster_overlay_area",
      ].includes(presentation) && (
        <UnitSelector
          unitType="area"
          value={unit as AreaUnit}
          unitDisplay={unitDisplay}
          onChange={(value: AreaUnit) =>
            onUpdate({
              componentSettings: { ...componentSettings, unit: value },
            })
          }
          onUnitDisplayChange={(display) =>
            onUpdate({
              componentSettings: { ...componentSettings, unitDisplay: display },
            })
          }
        />
      )}
      {(presentation === "distance_to_shore" ||
        presentation === "distance_to_features_over_water") && (
        <UnitSelector
          unitType="distance"
          value={unit as LengthUnit}
          unitDisplay={unitDisplay}
          onChange={(value: LengthUnit) =>
            onUpdate({
              componentSettings: { ...componentSettings, unit: value },
            })
          }
          onUnitDisplayChange={(display) =>
            onUpdate({
              componentSettings: { ...componentSettings, unitDisplay: display },
            })
          }
        />
      )}
      {presentation !== "count" && (
        <NumberRoundingControl
          value={componentSettings?.minimumFractionDigits}
          onChange={(minimumFractionDigits) =>
            onUpdate({
              componentSettings: {
                ...componentSettings,
                minimumFractionDigits,
              },
            })
          }
        />
      )}
      {/* {(presentation === "raster_stats" ||
        presentation === "geography_raster_stats") && (
        <UnitSelector
          unitType="distance"
          allowNone
          value={componentSettings?.unit as LengthUnit | undefined}
          unitDisplay={componentSettings?.unitDisplay}
          onChange={(value) =>
            onUpdate({
              componentSettings: { ...componentSettings, unit: value },
            })
          }
          onUnitDisplayChange={(display) =>
            onUpdate({
              componentSettings: { ...componentSettings, unitDisplay: display },
            })
          }
        />
      )} */}
      {(presentation === "geography_overlay_area" ||
        presentation === "geography_raster_stats" ||
        presentation === "geography_proportion_captured" ||
        presentation === "percent_count") && (
        <GeographySelector
          geographies={geographies}
          clippingGeography={clippingGeography}
          value={componentSettings.geographyId}
          onChange={(geographyId) => {
            if (geographyId === null) return;
            onUpdate({
              componentSettings: { ...componentSettings, geographyId },
            });
          }}
          t={t}
        />
      )}
      {(presentation === "column_values" ||
        presentation === "percent_column_total_overlapped") &&
        valueColumnOptions.length > 0 && (
          <LabeledDropdown
            label={t("column")}
            value={componentSettings?.column || ""}
            options={valueColumnOptions}
            onChange={handleValueColumnChange}
            getDisplayLabel={(selected) => selected?.value || ""}
          />
        )}
      {presentation === "column_values" && (
        <LabeledDropdown
          label={t("Stat")}
          value={selectedStat}
          options={statOptions}
          onChange={(val) => handleStatChange(val as ColumnValuesStatKey)}
        />
      )}
      {(presentation === "raster_stats" ||
        presentation === "geography_raster_stats") && (
        <LabeledDropdown
          label={t("Stat")}
          value={selectedRasterStat}
          options={rasterStatOptions}
          onChange={(val) => handleRasterStatChange(val as RasterValuesStatKey)}
        />
      )}
      {presentation === "raster_stats" && (
        <VrmSelector value={currentVrm} onChange={handleVrmChange} />
      )}
      {presentation === "column_values" && selectedStat === "countDistinct" && (
        <Popover.Root
          open={distinctLabelsModalOpen}
          onOpenChange={setDistinctLabelsModalOpen}
        >
          <Popover.Trigger asChild>
            <button
              type="button"
              className="h-6 bg-transparent text-gray-900 text-sm px-1 border-none rounded inline-flex items-center gap-1.5 hover:bg-gray-100 active:bg-gray-100 focus:bg-gray-100 data-[state=open]:bg-gray-100 focus:outline-none whitespace-nowrap"
            >
              <Pencil2Icon className="w-3 h-3" />
              {t("labeling")}
            </button>
          </Popover.Trigger>
          <TooltipPopoverContent>
            <div className="px-1 space-y-2 w-48">
              <p className="text-xs text-gray-500">
                {t(
                  "The following pluralized labels will be used depending on the count."
                )}
              </p>
              <div className="space-y-1">
                {pluralCategories.map((category) => (
                  <div key={category}>
                    <label className="text-xs text-gray-500">{category}</label>
                    <input
                      className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      type="text"
                      value={distinctCustomMessages?.[category] || ""}
                      placeholder={distinctDefaultMessages?.[category] || ""}
                      onChange={(e) =>
                        onUpdate({
                          componentSettings: {
                            ...componentSettings,
                            pluralizedDistinctValueLabels: {
                              ...componentSettings?.pluralizedDistinctValueLabels,
                              [lang.code]: {
                                ...componentSettings
                                  ?.pluralizedDistinctValueLabels?.[lang.code],
                                [category]: e.target.value,
                              },
                            },
                          },
                        })
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          </TooltipPopoverContent>
        </Popover.Root>
      )}
      {presentation === "count" && (
        <>
          <Popover.Root
            open={countLabelsModalOpen}
            onOpenChange={setCountLabelsModalOpen}
          >
            <Popover.Trigger asChild>
              <button
                type="button"
                className="h-6 bg-transparent text-gray-900 text-sm px-1 border-none rounded inline-flex items-center gap-1.5 hover:bg-gray-100 active:bg-gray-100 focus:bg-gray-100 data-[state=open]:bg-gray-100 focus:outline-none whitespace-nowrap"
              >
                <Pencil2Icon className="w-3 h-3" />
                {t("labeling")}
              </button>
            </Popover.Trigger>
            <TooltipPopoverContent>
              <div className="px-1 space-y-2 w-48">
                <TooltipBooleanConfigurationOption
                  label={t("show label")}
                  checked={!componentSettings?.hideLabelForCount}
                  onChange={(hideLabelForCount) =>
                    onUpdate({
                      componentSettings: {
                        ...componentSettings,
                        hideLabelForCount: !hideLabelForCount,
                      },
                    })
                  }
                />
                <div
                  className={
                    componentSettings?.hideLabelForCount
                      ? "opacity-20 pointer-events-none"
                      : ""
                  }
                >
                  <p className="text-xs text-gray-500">
                    {t(
                      "The following pluralized labels will be used depending on the count."
                    )}
                  </p>
                  <div className="space-y-1">
                    {pluralCategories.map((category) => (
                      <div key={category}>
                        <label className="text-xs text-gray-500">
                          {category}
                        </label>
                        <input
                          className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          type="text"
                          value={customMessages?.[category] || ""}
                          placeholder={defaultMessages?.[category] || ""}
                          onChange={(e) =>
                            onUpdate({
                              componentSettings: {
                                ...componentSettings,
                                pluralizedCountLabels: {
                                  ...componentSettings?.pluralizedCountLabels,
                                  [lang.code]: {
                                    ...componentSettings
                                      ?.pluralizedCountLabels?.[lang.code],
                                    [category]: e.target.value,
                                  },
                                },
                              },
                            })
                          }
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TooltipPopoverContent>
          </Popover.Root>
        </>
      )}
      <TooltipMorePopover>
        <div className="flex">
          <span className="text-sm font-light text-gray-400 whitespace-nowrap pr-1">
            {t("Metric Type")}
          </span>
          <span className="text-sm font-light  whitespace-nowrap px-1 flex-1 text-right">
            {type}
          </span>
        </div>

        {presentation === "distance_to_features_over_water" && (
          <>
            <DistanceToPointSearchInputs {...waterPathSearch} />
            <DistanceToPointResolutionField
              metrics={dependencies}
              onUpdateDependencyParameters={onUpdateDependencyParameters}
            />
            <LabeledDropdown
              label={t("label column")}
              value={componentSettings?.labelColumn || "none"}
              ariaLabel={t("label column")}
              truncateValue={false}
              modal={false}
              options={[
                { value: "none", label: t("None") },
                ...pointLabelColumnOptions,
              ]}
              onChange={(value) => {
                onUpdate({
                  componentSettings: {
                    ...componentSettings,
                    labelColumn: value === "none" ? null : value,
                  },
                });
                if (value === "none") return;
                onUpdateDependencyParameters((dependency) => {
                  if (dependency.type !== "distance_to_features_over_water") {
                    return { ...dependency.parameters };
                  }
                  return {
                    ...dependency.parameters,
                    includedColumns: [value],
                  };
                });
              }}
            />
          </>
        )}
        {relatedOverlay && (
          <>
            <div className="flex">
              <span className="text-sm font-light text-gray-400 whitespace-nowrap pr-1">
                {t("Layer")}
              </span>
              <span className="text-sm font-light whitespace-nowrap px-1 flex-1 text-right">
                {relatedOverlay.tableOfContentsItem?.title || "Unknown"}
              </span>
            </div>
          </>
        )}
        {(presentation === "count" ||
          presentation === "percent_count" ||
          presentation === "percent_count_total" ||
          presentation === "column_values" ||
          presentation === "percent_column_total_overlapped" ||
          presentation === "overlay_area" ||
          presentation === "raster_overlay_area" ||
          presentation === "geography_proportion_captured") && (
          <BufferSelector
            distanceKm={bufferSettings.distanceKm}
            bufferGeography={bufferSettings.bufferGeography}
            showBufferGeography={showBufferGeography}
            onChange={(next) => {
              onUpdateDependencyParameters((dependency) =>
                applyBufferSettingsToParameters(dependency, next)
              );
            }}
          />
        )}
        {(["overlay_area", "geography_overlay_area"].includes(presentation) ||
          isOverlayGeographyProportion) && (
          <label className="flex items-center gap-2 cursor-pointer pt-1">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={dependencies.some(
                (d) => d.parameters?.sourceHasOverlappingFeatures === true
              )}
              onChange={(e) => {
                const checked = e.target.checked;
                onUpdateDependencyParameters((dependency) => ({
                  ...(dependency.parameters || {}),
                  sourceHasOverlappingFeatures: checked || undefined,
                }));
              }}
            />
            <span className="text-sm font-light text-gray-400 whitespace-nowrap">
              {t("Source has overlapping features")}
            </span>
          </label>
        )}
        {presentation === "raster_overlay_area" && (
          <VrmSelector value={currentVrm} onChange={handleVrmChange} />
        )}
        {isRasterGeographyProportion && (
          <VrmSelector
            label={t("Sketch VRM")}
            value={currentVrm}
            onChange={handleVrmChange}
          />
        )}
        {(presentation === "geography_raster_stats" ||
          isRasterGeographyProportion) && (
          <VrmSelector
            geography
            value={currentGeographyVrm}
            onChange={handleGeographyVrmChange}
          />
        )}
      </TooltipMorePopover>
    </>
  );
};

function formatPresentationLabel(presentation: string) {
  switch (presentation) {
    case "total_area":
      return "Total Area";
    case "percent_area":
      return "Percent Area";
    case "distance_to_shore":
      return "Distance to Shore";
    case "distance_to_features_over_water":
      return "Distance to Point";
    case "overlay_area":
      return "Overlay Area";
    case "geography_overlay_area":
      return "Geography Overlap Area";
    case "count":
      return "Feature Count";
    case "percent_count":
      return "Percent of Geography Feature Count";
    case "percent_count_total":
      return "Count as % of Total";
    case "column_values":
      return "Column Statistic";
    case "percent_column_total_overlapped":
      return "% of Column Total";
    case "raster_stats":
      return "Raster Statistics";
    case "geography_raster_stats":
      return "Geography Raster Statistics";
    case "geography_proportion_captured":
      return "Geography Proportion Captured";
    case "raster_overlay_area":
      return "Raster Area Captured";
    default:
      return presentation;
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type LayerDetails = {
  bestLabelColumn?: string;
  labelSuitability: "good" | "poor" | "missing";
  bestCategoryColumn?: string;
  categorySuitability: "good" | "poor" | "missing";
};
